import { ComponentType } from "react";
import {
  PROGRAM_REGISTRIES,
  CONFIG_REGISTRY,
  type ProgramKey,
} from "../programContent/registries";

interface SectionConfig {
  id: string;
  title: string;
  componentPath: string;
  estimatedTime: number;
  description?: string;
}

interface ModuleConfig {
  moduleName: string;
  description: string;
  sections: SectionConfig[];
  totalTime: number;
  learningObjectives: string[];
}

interface ConfigModule {
  default: {
    [program: string]: {
      [week: string]: {
        [moduleType: string]: ModuleConfig;
      };
    };
  };
}

interface LoadedSection {
  id: string;
  component: ComponentType<any>;
  config: SectionConfig;
}

class ModuleContentLoader {
  private static componentCache = new Map<string, ComponentType<any>>();
  private static configCache = new Map<string, ModuleConfig>();
  private static registryCache = new Map<string, any>();

  // Load module configuration
  static async loadModuleConfig(
    program: string,
    week: string,
    moduleType: string
  ): Promise<ModuleConfig | null> {
    const cacheKey = `${program}_${week}_${moduleType}`;

    if (this.configCache.has(cacheKey)) {
      return this.configCache.get(cacheKey)!;
    }

    try {
      const programKey = program.toLowerCase() as ProgramKey;

      if (!CONFIG_REGISTRY[programKey]) {
        throw new Error(`Unknown program: ${program}`);
      }

      const configModule: any = await CONFIG_REGISTRY[programKey]();

      // Handle both default export and direct export
      const configData = configModule.default || configModule;
      const moduleConfig = configData[program]?.[week]?.[moduleType];

      if (!moduleConfig) {
        throw new Error(`Config not found: ${program}/${week}/${moduleType}`);
      }

      this.configCache.set(cacheKey, moduleConfig);
      return moduleConfig;
    } catch (error) {
      console.error(`Failed to load config for ${cacheKey}:`, error);
      return null;
    }
  }

  // Load program-specific component registry
  private static async loadProgramRegistry(program: string) {
    if (this.registryCache.has(program)) {
      return this.registryCache.get(program);
    }

    try {
      const programKey = program.toLowerCase() as ProgramKey;

      if (!PROGRAM_REGISTRIES[programKey]) {
        throw new Error(`Unknown program: ${program}`);
      }

      const registry = await PROGRAM_REGISTRIES[programKey]();
      this.registryCache.set(program, registry);
      return registry;
    } catch (error) {
      console.error(`Failed to load registry for program: ${program}`, error);
      throw error;
    }
  }

  // Dynamically load section components
  static async loadSectionComponents(
    sections: SectionConfig[]
  ): Promise<LoadedSection[]> {
    // Group sections by program for efficient registry loading
    const sectionsByProgram = sections.reduce((acc, section) => {
      const program = section.componentPath.split("/")[0];
      if (!acc[program]) acc[program] = [];
      acc[program].push(section);
      return acc;
    }, {} as Record<string, SectionConfig[]>);

    const allResults: PromiseSettledResult<LoadedSection>[] = [];

    // Process each program separately
    for (const [program, programSections] of Object.entries(
      sectionsByProgram
    )) {
      try {
        const registry = await this.loadProgramRegistry(program);

        const programResults = await Promise.allSettled(
          programSections.map(async (section): Promise<LoadedSection> => {
            const cacheKey = section.componentPath;

            // Check cache first
            if (this.componentCache.has(cacheKey)) {
              return {
                id: section.id,
                component: this.componentCache.get(cacheKey)!,
                config: section,
              };
            }

            try {
              const componentPath = section.componentPath;

              if (!registry[componentPath]) {
                throw new Error(
                  `Component not registered: ${section.componentPath}`
                );
              }

              const componentModule = await registry[componentPath]();
              const Component =
                componentModule.default || componentModule[section.id];

              if (!Component) {
                throw new Error(
                  `Component not found in module: ${section.componentPath}`
                );
              }

              this.componentCache.set(cacheKey, Component);

              return {
                id: section.id,
                component: Component,
                config: section,
              };
            } catch (error) {
              console.warn(
                `Failed to load component: ${section.componentPath}`,
                error
              );

              return {
                id: section.id,
                component: this.getFallbackComponent(
                  section.title,
                  error as Error
                ),
                config: section,
              };
            }
          })
        );

        allResults.push(...programResults);
      } catch (error) {
        console.error(`Failed to load registry for program: ${program}`, error);

        // Create fallback components for all sections in this program
        const fallbackResults = programSections.map((section) => ({
          status: "fulfilled" as const,
          value: {
            id: section.id,
            component: this.getFallbackComponent(section.title, error as Error),
            config: section,
          },
        }));

        allResults.push(...fallbackResults);
      }
    }

    return allResults
      .filter(
        (result): result is PromiseFulfilledResult<LoadedSection> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);
  }

  // Fallback component for missing content
  private static getFallbackComponent(
    title: string,
    error?: Error
  ): ComponentType<any> {
    return () => {
      const React = require("react");
      const { Box, Typography, Alert, Button } = require("@mui/material");

      return React.createElement(
        Box,
        { p: 3, textAlign: "center" },
        React.createElement(
          Typography,
          { variant: "h6", color: "textSecondary" },
          title
        ),
        React.createElement(
          Typography,
          { variant: "body2", color: "textSecondary", sx: { mt: 1 } },
          "Content coming soon..."
        ),
        error &&
          React.createElement(
            Alert,
            { severity: "warning", sx: { mt: 2 } },
            `Missing component: ${error.message}`
          ),
        React.createElement(
          Button,
          {
            variant: "outlined",
            size: "small",
            sx: { mt: 2 },
            onClick: () => window.location.reload(),
          },
          "Refresh Page"
        )
      );
    };
  }

  // Clear cache for memory management
  static clearCache(): void {
    this.componentCache.clear();
    this.configCache.clear();
    this.registryCache.clear();
  }

  // Preload next module for better UX
  static async preloadModule(
    program: string,
    week: string,
    moduleType: string
  ): Promise<void> {
    try {
      const config = await this.loadModuleConfig(program, week, moduleType);
      if (config?.sections) {
        // Preload program registry if not already loaded
        await this.loadProgramRegistry(program);

        // Preload components in background
        this.loadSectionComponents(config.sections).catch(() => {
          // Silent fail for preloading
        });
      }
    } catch (error) {
      console.debug("Preload failed:", error);
    }
  }

  // Get cache statistics for debugging
  static getCacheInfo(): {
    components: number;
    configs: number;
    registries: number;
  } {
    return {
      components: this.componentCache.size,
      configs: this.configCache.size,
      registries: this.registryCache.size,
    };
  }

  // Check if program is supported
  static isProgramSupported(program: string): boolean {
    const programKey = program.toLowerCase() as ProgramKey;
    return programKey in PROGRAM_REGISTRIES;
  }
}

export default ModuleContentLoader;
export type { ModuleConfig, SectionConfig, LoadedSection };
